import { clsx, type ClassValue } from "clsx"
import { endOfMonth, format, startOfMonth } from "date-fns";
import { twMerge } from "tailwind-merge"
import { isEqual, startOfDay, isAfter, isBefore } from "date-fns";
import { ScheduledPostResponse, SocialPostType } from "@/features/approval-view/api/socialpost-api";
import { PendingFilterType } from "@/features/approval-view";
import { DateRange } from "react-day-picker";
import { DateTime } from "luxon";
import { SuggestedPostFilterType } from "@/features/previous-post-view/unreviewed/UnreviewedTab";
import { SocialPostType as SuggestedSocialPostType } from "@/features/previous-post-view/types";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function getLastYearDateRange(): { start_date: string, end_date: string } {
  const today = new Date(); // Current date
  const currentMonth = today.getMonth(); // Current month (0-indexed)
  const lastYear = today.getFullYear() - 1;

  // Start date: First day of the current month, last year
  const startDate = new Date(lastYear, currentMonth, 1);

  // End date: Last day of the current month, last year
  const endDate = new Date(lastYear, currentMonth + 1, 0); // Using 0 gives the last day of the previous month

  return {
    start_date: format(startDate, 'yyyy-MM-dd'),
    end_date: format(endDate, 'yyyy-MM-dd'),
  }

}


export const filterResults = (filter: PendingFilterType, posts: SocialPostType[]): SocialPostType[] => {
  if (filter.name === "" && !filter.endDate && !filter.startDate && !filter.type) {
    return posts;
  }

  let filteredPosts: SocialPostType[] = posts;

  if (filter.name !== "") {
    filteredPosts = filteredPosts.filter(post =>
      post.event.toLowerCase().includes(filter.name.toLowerCase())
    );
  }

  if (filter.startDate) {
    filteredPosts = filteredPosts.filter(post => {
      if (!filter.startDate) return false;
      const postStartDate = new Date(post.start_date);
      const postEndDate = new Date(post.end_date);
      const filterStartDate = startOfDay(filter.startDate);
      return isAfter(postStartDate, filterStartDate) || isEqual(postStartDate, filterStartDate) || isBefore(filterStartDate, postEndDate);
    });
  }

  if (filter.endDate) {
    filteredPosts = filteredPosts.filter(post => {
      if (!filter.endDate) return false;
      const postStartDate = new Date(post.start_date);
      const postEndDate = new Date(post.end_date);
      const filterEndDate = startOfDay(filter.endDate);
      return isBefore(postEndDate, filterEndDate) || isEqual(postEndDate, filterEndDate) || isAfter(filterEndDate, postStartDate);
    });
  }

  if (filter.type) {
    filteredPosts = filteredPosts.filter(post =>
      post.post_type.toLowerCase() === filter.type!.toLowerCase()
    );
  }

  console.log(filteredPosts);
  return filteredPosts;
};

export const extractAttr = (str: string): string => {
  const parts = str.split('.');
  const lastPart = parts.pop() ?? ''; // Use nullish coalescing to handle undefined
  return lastPart
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const returnDefinedDates = (dateRange: DateRange | undefined): { start: Date, end: Date } => {
  if (dateRange && dateRange.from && dateRange.to) {
    return {
      start: dateRange.from,
      end: dateRange.to
    }
  }
  if (dateRange && (!dateRange.from && dateRange.to)) {
    return {
      start: startOfMonth(dateRange.to),
      end: dateRange.to
    }
  }
  if (dateRange && (dateRange.from && !dateRange.to)) {
    return {
      start: dateRange.from,
      end: endOfMonth(dateRange.from)
    }
  }
  return {
    start: startOfMonth(new Date()),
    end: endOfMonth(new Date())
  }

}
// Filter for scheduled posts page
export const filterScheduledResults = (data: ScheduledPostResponse[], name: string): ScheduledPostResponse[] => {
  if (!name || name === "") {
    return data;
  }
  const filteredData: ScheduledPostResponse[] = []

  for (let i = 0; i < data.length; i++) {
    const currentObj: ScheduledPostResponse = { date: data[i].date, posts: [] }
    for (let j = 0; j < data[i].posts.length; j++) {
      if (data[i].posts[j].event_name === name) {
        currentObj.posts.push(data[i].posts[j]);
        filteredData.push(currentObj)
      }
    }
  }

  return filteredData
}

export const getSlicedWord = (word: string): string => {
  if ((window.innerWidth > 1280 && window.innerWidth < 1440) || window.innerWidth < 768) {
    return word.slice(0, 20)
  };
  if (window.innerWidth > 1024 && window.innerWidth < 1280) {
    return word.slice(0, 10)
  }
  return word
}

export const extractEventName = (data: SocialPostType[]): { name: string, id: number }[] => {
  const arr = data.map(event => {
    return { name: event.event, id: event.event_id }
  })
  return arr
}

export const findEventDate = (data: SocialPostType[], name: string): { start_date: string, end_date: string } => {
  if (name === "" || data.length === 0) {
    return { start_date: format(new Date(), 'yyyy-MM-dd'), end_date: format(new Date(), 'yyyy-MM-dd') }
  }
  const foundIndex = data.findIndex(event => event.event === name);
  if (foundIndex === -1) {
    return { start_date: format(new Date(), 'yyyy-MM-dd'), end_date: format(new Date(), 'yyyy-MM-dd') }
  }
  return {
    start_date: data[foundIndex].start_date,
    end_date: data[foundIndex].end_date
  }
}

export const filterSuggestedResults = (filter: SuggestedPostFilterType, posts: SuggestedSocialPostType[]): SuggestedSocialPostType[] => {
  if (filter.name === "" && !filter.endDate && !filter.startDate && !filter.type && filter.event_category.length === 0) {
    return posts;
  }

  console.log(filter)
  let filteredPosts: SuggestedSocialPostType[] = posts;

  if (filter.name !== "") {
    filteredPosts = filteredPosts.filter(post =>
      post.event.toLowerCase().includes(filter.name.toLowerCase())
    );
  }

  if (filter.startDate) {
    filteredPosts = filteredPosts.filter(post => {
      if (!filter.startDate) return false;
      const postStartDate = new Date(post.start_date);
      const postEndDate = new Date(post.end_date);
      const filterStartDate = startOfDay(filter.startDate);
      return isAfter(postStartDate, filterStartDate) || isEqual(postStartDate, filterStartDate) || isBefore(filterStartDate, postEndDate);
    });
  }

  if (filter.endDate) {
    filteredPosts = filteredPosts.filter(post => {
      if (!filter.endDate) return false;
      const postStartDate = new Date(post.start_date);
      const postEndDate = new Date(post.end_date);
      const filterEndDate = startOfDay(filter.endDate);
      return isBefore(postEndDate, filterEndDate) || isEqual(postEndDate, filterEndDate) || isAfter(filterEndDate, postStartDate);
    });
  }

  if (filter.type) {
    filteredPosts = filteredPosts.filter(post =>
      post.post_type.toLowerCase() === filter.type!.toLowerCase()
    );
  }
  if (filter.event_category.length !== 0) {
    filteredPosts = filteredPosts.filter(post => filter.event_category.includes(post.event_category))
  }

  console.log(filteredPosts);
  return filteredPosts;
};


export const extractCategories = (data: SuggestedSocialPostType[]): string[] => {
  const str: string[] = [];

  data.forEach(event => {
    const isPushed = str.findIndex(strCat => strCat === event.event_category)
    if (event.event_category !== null && isPushed === -1) {
      str.push(event.event_category)
    }
  })

  return str

}

export const getTimelineFilters = (timeline: {
  post_id: number;
  isPosted: boolean;
  post_title: string;
  posting_date: string;
  platform: string;
}[]): string[] => {
  const filtersList: string[] = []

  timeline.forEach(post => {
    if (filtersList.indexOf(post.platform) === -1) {
      filtersList.push(post.platform)
    }
  })

  return filtersList
}

// Function to get true date without timezones
export const getTrueDate = (dateString: string, formatString: string) => {
 const date = DateTime.fromISO(dateString, { zone: 'America/New_York' });

  switch (formatString) {
    case 'P':
      return date.toLocaleString(DateTime.DATE_SHORT); // e.g., 7/24/25
    case 'PP':
      return date.toLocaleString(DateTime.DATE_MED);   // e.g., Jul 24, 2025
    case 'PPP':
      return date.toLocaleString(DateTime.DATE_FULL);  // e.g., July 24, 2025
    case 'PPPP':
      return date.toLocaleString(DateTime.DATE_HUGE);  // e.g., Thursday, July 24, 2025
    default:
      return date.toFormat(formatString); // For custom patterns like 'yyyy-MM-dd'
  }
};
